import { AuthState, User, LoginResponse } from '../types/auth';
import { apiService } from './api';
import { setToken, setUser, removeToken, getUser, isAuthenticated } from './config';
import { utils } from '../utils';

/**
 * Serviço de autenticação para gerenciamento de sessão do usuário
 */
export class AuthService {
  private static instance: AuthService;
  private state: AuthState = {
    isAuthenticated: false,
    isLoading: false,
    user: null,
    error: null
  };

  private constructor() {
    this.initialize();
  }

  /**
   * Obter instância única do serviço (Singleton)
   */
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Inicializa o serviço de autenticação
   */
  private initialize(): void {
    // Verificar se usuário já está autenticado
    if (isAuthenticated()) {
      this.state.isAuthenticated = true;
      this.state.user = getUser();
    }

    // Iniciar monitoramento de token
    this.startTokenWatch();
  }

  /**
   * Realiza o processo de login
   */
  async login(email: string, password: string): Promise<void> {
    this.setLoading(true);
    this.clearError();

    try {
      // Validação básica
      if (!this.validateLoginForm(email, password)) {
        return;
      }

      const payload = { email, senha: password };
      const response = await apiService.login(payload);

      // Verificar se o login foi bem-sucedido
      if (response.success && response.token && response.usuario) {
        await this.handleLoginSuccess(response);
      } else {
        throw new Error('Resposta inválida do servidor');
      }

    } catch (error: any) {
      this.handleLoginError(error as Error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Realiza logout do usuário
   */
  async logout(): Promise<void> {
    try {
      // Chamar API de logout
      await apiService.post('LOGOUT', {});
    } catch (error) {
      // Continuar mesmo com erro na API
    } finally {
      // Limpar dados locais independente do resultado da API
      this.handleLogout();
    }
  }

  /**
   * Manipula login bem-sucedido
   */
  private async handleLoginSuccess(response: LoginResponse): Promise<void> {
    // Salvar token e informações do usuário
    setToken(response.token);
    setUser(response.usuario);

    // Atualizar estado
    this.state.isAuthenticated = true;
    this.state.user = response.usuario;
    this.state.error = null;

    utils.showNotification(response.message || 'Login realizado com sucesso!', { type: 'success' });
  }

  /**
   * Manipula logout
   */
  private handleLogout(): void {
    // Limpar dados locais
    removeToken();

    // Atualizar estado
    this.state.isAuthenticated = false;
    this.state.user = null;
    this.state.error = null;

    utils.showNotification('Logout realizado com sucesso!', { type: 'success' });
  }

  /**
   * Manipula erro no login
   */
  private handleLoginError(error: Error): void {
    let errorMessage = 'Erro desconhecido no login';

    if (error.message.includes('401')) {
      errorMessage = 'Email ou senha incorretos.';
    } else if (error.message.includes('500')) {
      errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
    } else if (error.message.includes('Failed to fetch')) {
      errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
    } else {
      errorMessage = error.message;
    }

    this.state.error = errorMessage;
    utils.showNotification(errorMessage, { type: 'error' });
  }

  /**
   * Valida o formulário de login
   */
  private validateLoginForm(email: string, password: string): boolean {
    this.clearError();

    if (!email || !password) {
      this.state.error = 'Por favor, preencha todos os campos.';
      utils.showNotification(this.state.error, { type: 'error' });
      return false;
    }

    if (!utils.isValidEmail(email)) {
      this.state.error = 'Por favor, digite um e-mail válido.';
      utils.showNotification(this.state.error, { type: 'error' });
      return false;
    }

    return true;
  }

  /**
   * Verifica periodicamente se o token expirou
   */
  private startTokenWatch(): void {
    setInterval(() => {
      if (!isAuthenticated() && this.state.isAuthenticated) {
        this.logout();
      }
    }, 60000); // Verificar a cada minuto
  }

  /**
   * Define estado de carregamento
   */
  private setLoading(loading: boolean): void {
    this.state.isLoading = loading;
  }

  /**
   * Limpa mensagens de erro
   */
  clearError(): void {
    this.state.error = null;
  }

  /**
   * Verifica se usuário está autenticado
   */
  checkAuth(): boolean {
    const isAuth = isAuthenticated();
    if (!isAuth && this.state.isAuthenticated) {
      // Se o estado estava como autenticado mas o token expirou
      this.state.isAuthenticated = false;
      this.state.user = null;
    }
    return isAuth;
  }

  /**
   * Obtém o estado atual da autenticação
   */
  getState(): AuthState {
    return { ...this.state };
  }

  /**
   * Obtém informações do usuário logado
   */
  getUser(): User | null {
    return this.state.user;
  }

  /**
   * Verifica se está carregando
   */
  isLoading(): boolean {
    return this.state.isLoading;
  }

  /**
   * Obtém erro atual
   */
  getError(): string | null {
    return this.state.error;
  }

  /**
   * Verifica se está autenticado
   */
  isAuthenticated(): boolean {
    return this.state.isAuthenticated;
  }

  /**
   * Atualiza os dados do usuário no estado e no localStorage
   */
  updateUser(userData: Partial<User>): void {
    if (!this.state.user) {
      return;
    }

    // Atualizar o usuário mesclando os dados novos com os existentes
    const updatedUser = { ...this.state.user, ...userData };

    // Salvar no localStorage
    setUser(updatedUser);

    // Atualizar o estado interno
    this.state.user = updatedUser;
  }

  /**
   * Redireciona para login
   */
  redirectToLogin(): void {
    // Implementar redirecionamento para página de login
    // window.location.href = '/login'; // Implementar quando tiver roteamento
  }

  /**
   * Redireciona para o sistema
   */
  redirectToSystem(): void {
    // Implementar redirecionamento para sistema
    // window.location.href = '/dashboard'; // Implementar quando tiver roteamento
  }

  /**
   * Solicita recuperação de senha
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await apiService.forgotPassword(email);
      utils.showNotification('Email de recuperação enviado! Verifique sua caixa de entrada.', { type: 'success' });
    } catch (error: any) {
      const errorMessage = error.message || 'Erro ao enviar email de recuperação. Tente novamente.';
      utils.showNotification(errorMessage, { type: 'error' });
      throw error;
    }
  }

  /**
   * Recarrega as permissões do usuário do backend
   * Útil quando admin concede/revoga permissões e o usuário está logado
   */
  async refreshPermissions(): Promise<void> {
    try {
      // Buscar dados atualizados do usuário no backend
      const response = await apiService.get('PERFIL') as any;

      if (response) {
        // O backend retorna o usuário diretamente, não dentro de { usuario: ... }
        const updatedUser = response as User;

        // Atualizar usuário no localStorage
        setUser(updatedUser);

        // Atualizar estado interno
        this.state.user = updatedUser;

        utils.showNotification('Permissões atualizadas!', { type: 'success' });
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Erro ao atualizar permissões. Tente novamente.';
      utils.showNotification(errorMessage, { type: 'error' });
      throw error;
    }
  }
}

// Exportar instância única do serviço
export const authService = AuthService.getInstance();
